import { useRef } from 'react';
import { object, string, type AnySchema } from 'yup';
import { useTextareaAutoSize } from '../../../hooks/useTextareaAutoSize';
import { BaseReportElement } from '../../BaseReportElement';
import type {
    BuildStructureSettingsYupSchemaHelpers,
    ContextForKind,
    ElementDefinitionWithSettings,
    ElementContentRenderResult,
    ElementSettings,
    EditorRenderRuntime,
    RenderContextKind,
    RenderRuntime,
} from '../../types';
import type { StructurePath } from '../../structurePath';

interface PlainTextSettingsInput extends ElementSettings {
    text: unknown;
}

interface PlainTextSettings extends ElementSettings {
    text: string;
}

export type PlainTextDefinition = ElementDefinitionWithSettings<'plain_text', { text: string }>;

const DEFAULT_PLAIN_TEXT_CONTENT = '';

function PlainTextEditor({
    runtime,
    elementPath,
    text,
}: {
    runtime: EditorRenderRuntime;
    elementPath: StructurePath;
    text: string;
}): JSX.Element {
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);
    useTextareaAutoSize(textareaRef, text);

    return (
        <textarea
            ref={textareaRef}
            value={text}
            placeholder={gettext('REPORT_BUILDER_TEXT_ELEMENT_PLACEHOLDER')}
            rows={1}
            className="w-full resize-none overflow-hidden bg-transparent px-0 py-0 text-base leading-6 text-slate-800 outline-none"
            onChange={(event) => {
                runtime.editor.structureController.updateSettingsAtPath(elementPath, {
                    text: event.currentTarget.value,
                });
            }}
        />
    );
}

export function createPlainTextDefinition(text: string): PlainTextDefinition {
    return {
        type: 'plain_text',
        settings: { text },
    };
}

export class PlainTextElement extends BaseReportElement<
    RenderContextKind,
    PlainTextSettingsInput,
    PlainTextSettings
> {
    static readonly elementType = 'plain_text';

    static readonly layout = { flow: 'fixed' } as const;

    static readonly compatibleContextKinds = ['project', 'measuring-point'] as const;

    static get insertion() {
        return {
            label: gettext('REPORT_BUILDER_TEXT_ELEMENT_LABEL'),
            hiddenFromMenu: true,
            supportedSlots: ['child-list'] as const,
            createDefinition: () => createPlainTextDefinition(DEFAULT_PLAIN_TEXT_CONTENT),
        };
    }

    static buildStructureSettingsYupSchema(
        _helpers: BuildStructureSettingsYupSchemaHelpers
    ): AnySchema {
        return object({
            text: string().defined(),
        })
            .required()
            .noUnknown();
    }

    protected toCompatibleReactNode(
        _ctx: ContextForKind<RenderContextKind>,
        runtime: RenderRuntime
    ): ElementContentRenderResult {
        const currentLocation = runtime.getCurrentElementLocation();
        if (!currentLocation) {
            throw new Error('Missing plain_text location in render runtime.');
        }

        return {
            node: runtime.showEditor ? (
                <PlainTextEditor
                    runtime={runtime}
                    elementPath={currentLocation.elementPath}
                    text={this.settings.text}
                />
            ) : (
                <div className="whitespace-pre-wrap break-words text-base text-slate-800">
                    {this.settings.text}
                </div>
            ),
            ready: Promise.resolve(),
            hasRenderableContent: Promise.resolve(true),
        };
    }
}
